import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Cards,
  type CardsProps,
  Link,
  SpaceBetween,
  StatusIndicator,
  Table,
} from '@cloudscape-design/components';
import type { CodeEvidence, EvaluationResult } from '@amzn/helianthus-arbiter-client';
import { useCollection } from '@cloudscape-design/collection-hooks';
import { useNavigate } from 'react-router';
import {
  OUTCOME_LABEL,
  OUTCOME_RANK,
  buildRuleUrl,
  compareRuleGroupByOutcome,
  compareRuleGroupByResultCount,
  compareRuleGroupBySeverity,
  getOutcomeType,
  groupResultsByRule,
  type EvaluationOutcome,
  type RuleGroup,
} from '../utils/evaluationHelpers';
import { useUrlSearchState } from '../hooks/useUrlSearchState';
import { useSplitPanel } from '../context/SplitPanelContext';
import { AssignedSeverity } from './AssignedSeverity';
import { ConfidenceBadge } from './ConfidenceBadge';
import { DryRunBadge } from './DryRunBadge';
import { ResultDisclosure } from './ResultDisclosure';
import { SimpleMarkdown } from '../utils/simpleMarkdown';

interface RuleResultsPanelProps {
  results: EvaluationResult[];
}

const DASH = '—';

const RESULT_CARDS_PER_ROW = [{ cards: 1 }];

// Worst outcome first, as in the Rules table, so a failure never sits below a suppressed pass.
function sortWorstFirstThenByConfidence(results: EvaluationResult[]): EvaluationResult[] {
  return [...results].sort((a, b) => {
    const rank =
      OUTCOME_RANK[a.outcome as EvaluationOutcome] - OUTCOME_RANK[b.outcome as EvaluationOutcome];
    if (rank !== 0) return rank;
    if (a.confidence == null) return b.confidence == null ? 0 : 1;
    if (b.confidence == null) return -1;
    return b.confidence - a.confidence;
  });
}

function formatLocation(code: CodeEvidence | undefined): string | null {
  if (code?.filePath == null || code.startLine == null) return null;
  if (code.endLine != null && code.endLine !== code.startLine) {
    return `${code.filePath}:${code.startLine}-${code.endLine}`;
  }
  return `${code.filePath}:${code.startLine}`;
}

// DISPUTED reads as "Suppressed": it covers both false positives and real findings reported elsewhere.
const VERDICT_LABEL: Record<string, string> = {
  DISPUTED: 'Suppressed',
  CONFIRMED: 'Confirmed',
  PENDING: 'Not reviewed',
};

function ValidationSummary({ result }: { result: EvaluationResult }): React.ReactNode {
  const label = result.validationVerdict && VERDICT_LABEL[result.validationVerdict];
  // The judge reviews only failures; any other result is confirmed without being looked at.
  if (!label || (result.validationVerdict !== 'PENDING' && result.originalOutcome !== 'FAIL')) {
    return DASH;
  }
  const reasoning =
    result.validationVerdict === 'PENDING'
      ? result.isDryRun
        ? 'Dry-run results are not reviewed by the validation judge.'
        : 'Not yet reviewed by the validation judge.'
      : result.validationReasoning;
  return (
    <SpaceBetween size="xxs">
      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
        <Badge color="grey">{label}</Badge>
        {result.originalOutcome && result.outcome && result.originalOutcome !== result.outcome && (
          <span>
            <StatusIndicator type={getOutcomeType(result.originalOutcome as EvaluationOutcome)}>
              {OUTCOME_LABEL[result.originalOutcome] ?? result.originalOutcome}
            </StatusIndicator>{' '}
            <span>→</span>{' '}
            <StatusIndicator type={getOutcomeType(result.outcome as EvaluationOutcome)}>
              {OUTCOME_LABEL[result.outcome] ?? result.outcome}
            </StatusIndicator>
          </span>
        )}
      </SpaceBetween>
      {reasoning && <Box variant="p">{reasoning}</Box>}
    </SpaceBetween>
  );
}

const RESULT_CARD_DEFINITION: CardsProps.CardDefinition<EvaluationResult> = {
  header: (item) => (
    <SpaceBetween direction="horizontal" size="xs" alignItems="center">
      <span>{item.title}</span>
      {item.outcome && (
        <StatusIndicator type={getOutcomeType(item.outcome as EvaluationOutcome)}>
          {OUTCOME_LABEL[item.outcome] ?? item.outcome}
        </StatusIndicator>
      )}
    </SpaceBetween>
  ),
  sections: [
    {
      id: 'location',
      header: 'Location',
      width: 50,
      content: (item) => formatLocation(item.evidence?.code) ?? DASH,
    },
    {
      id: 'confidence',
      header: 'Confidence',
      width: 25,
      content: (item) =>
        item.confidence != null ? <ConfidenceBadge confidence={item.confidence} /> : DASH,
    },
    {
      id: 'cwe',
      header: 'CWE',
      width: 25,
      content: (item) =>
        item.cweIds && item.cweIds.length > 0 ? (
          <SpaceBetween direction="horizontal" size="xxs">
            {item.cweIds.map((cwe) => (
              <Badge key={cwe}>{cwe}</Badge>
            ))}
          </SpaceBetween>
        ) : (
          DASH
        ),
    },
    {
      id: 'assignedSeverity',
      header: 'Assessed severity',
      width: 100,
      content: (item) => (item.assignedSeverity ? <AssignedSeverity result={item} /> : DASH),
    },
    {
      id: 'validation',
      header: 'Validation',
      width: 100,
      content: (item) => <ValidationSummary result={item} />,
    },
    {
      id: 'reasoning',
      header: 'Reasoning',
      width: 100,
      content: (item) => (item.description ? <SimpleMarkdown content={item.description} /> : DASH),
    },
    {
      id: 'snippet',
      header: 'Snippet',
      width: 100,
      content: (item) => (item.evidence?.code?.snippet ? <ResultDisclosure result={item} /> : DASH),
    },
    {
      id: 'remediation',
      header: 'Remediation',
      width: 100,
      content: (item) => (item.remediation ? <SimpleMarkdown content={item.remediation} /> : DASH),
    },
  ],
};

const RESULT_CARD_SECTION_IDS = (RESULT_CARD_DEFINITION.sections ?? []).map((s) => s.id!);

function RuleResultCards({ group }: { group: RuleGroup }): React.JSX.Element {
  return (
    <Cards<EvaluationResult>
      items={sortWorstFirstThenByConfidence(group.results)}
      cardsPerRow={RESULT_CARDS_PER_ROW}
      cardDefinition={RESULT_CARD_DEFINITION}
      // Absent adjudication data is all-or-nothing per evaluation, so hide the empty section.
      visibleSections={
        group.results.some((r) => r.validationVerdict)
          ? undefined
          : RESULT_CARD_SECTION_IDS.filter((id) => id !== 'validation')
      }
    />
  );
}

export function RuleResultsPanel({ results }: RuleResultsPanelProps): React.JSX.Element {
  const groups = useMemo(() => groupResultsByRule(results), [results]);
  const urlState = useUrlSearchState();
  const navigate = useNavigate();
  const { show, clear } = useSplitPanel();

  const { items, collectionProps } = useCollection(groups, { sorting: {} });

  const [selectedRuleId, setSelectedRuleId] = useState<string | undefined>(() => {
    const fromUrl = urlState.get('rule');
    return groups.some((g) => g.ruleId === fromUrl) ? fromUrl : undefined;
  });

  const selectedGroup = groups.find((g) => g.ruleId === selectedRuleId);

  useEffect(() => {
    if (selectedGroup) {
      show(selectedGroup.ruleId, <RuleResultCards group={selectedGroup} />);
    }
  }, [selectedGroup, show]);

  useEffect(() => clear, [clear]);

  return (
    <Table<RuleGroup>
      {...collectionProps}
      variant="embedded"
      trackBy="ruleId"
      items={items}
      selectionType="single"
      selectedItems={selectedGroup ? [selectedGroup] : []}
      onSelectionChange={({ detail }) => {
        const next = detail.selectedItems[0] as RuleGroup | undefined;
        setSelectedRuleId(next?.ruleId);
        urlState.set('rule', next?.ruleId ?? '');
      }}
      empty={<Box variant="p">No rule results.</Box>}
      columnDefinitions={[
        {
          id: 'name',
          header: 'Name',
          sortingField: 'ruleName',
          cell: (group) => {
            const ruleUrl = buildRuleUrl(group.ruleName, group.version);
            return (
              <SpaceBetween direction="horizontal" size="s" alignItems="center">
                <Link
                  href={ruleUrl}
                  onFollow={(e) => {
                    e.preventDefault();
                    navigate(ruleUrl);
                  }}
                >
                  {group.ruleName}
                </Link>
                {group.results.length > 1 && <Badge>{String(group.results.length)}</Badge>}
                {group.results.some((r) => r.isDryRun) && <DryRunBadge />}
              </SpaceBetween>
            );
          },
        },
        {
          id: 'outcome',
          header: 'Outcome',
          sortingComparator: compareRuleGroupByOutcome,
          cell: (group) => (
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              <StatusIndicator type={getOutcomeType(group.outcome)}>
                {OUTCOME_LABEL[group.outcome] ?? group.outcome}
              </StatusIndicator>
              {group.results.some((r) => r.validationVerdict === 'DISPUTED') && (
                <Badge color="grey">{VERDICT_LABEL.DISPUTED}</Badge>
              )}
            </SpaceBetween>
          ),
        },
        {
          id: 'severity',
          header: 'Severity',
          sortingComparator: compareRuleGroupBySeverity,
          cell: (group) => group.severity ?? '--',
        },
        {
          id: 'results',
          header: 'Results',
          sortingComparator: compareRuleGroupByResultCount,
          cell: (group) => group.results.length,
        },
      ]}
    />
  );
}
